/**
 * useProgressStream — owns the SSE execution channel for a list execution.
 *
 * The stream endpoint is the ONLY execution request in the new frontend flow:
 * it streams progress AND delivers the authoritative `ListExecution` inside
 * its terminal `completed` event.
 *
 * Lifecycle (matching the cartCreation SSE precedent):
 *  - `AbortController` stored in a ref, aborted on unmount (useEffect cleanup).
 *  - A terminal SSE event (`completed` / `failed`) closes the stream
 *    automatically.
 *  - `startProgress` returns `{ controller, result }`: the controller lets the
 *    caller force-close the stream, and `result` is a promise resolving with
 *    the final `ListExecution` (or `null` when the stream ended without one).
 *  - `abortProgress` closes the stream without clearing the progress state
 *    (the overlay keeps showing the last step until the result lands).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
    executeListStream,
    ExecuteListStreamOptions,
} from "modules/lists/services/listService";
import { ListExecution, ProgressState } from "modules/lists/types/lists";

const TERMINAL_STEPS = new Set<ProgressState["step"]>(["completed", "failed"]);

/**
 * Returns progress state + lifecycle controls for the SSE execution stream.
 * @returns `progress` latest parsed event, `startProgress` to open the stream
 * (returns the AbortController plus a result promise carrying the final
 * `ListExecution`), `abortProgress` to close it, and `resetProgress` to clear
 * the last event.
 */
export const useProgressStream = () => {
    const abortControllerRef = useRef<AbortController | null>(null);
    const [progress, setProgress] = useState<ProgressState | null>(null);

    // Unmount cleanup — close any open SSE connection (cartCreation pattern).
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const abortProgress = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    }, []);

    const resetProgress = useCallback(() => {
        setProgress(null);
    }, []);

    /**
     * Opens the SSE execution stream for a list.
     * @param listId UUID of the list being executed.
     * @param payload Optional execute payload (excluded ids, quantity
     * overrides, temporary items) forwarded in the POST body.
     * @returns The AbortController driving the stream, plus a `result` promise
     * resolving with the final ListExecution delivered by the terminal
     * `completed` event — or `null` when the stream ended without one.
     */
    const startProgress = useCallback(
        (listId: string, payload?: Omit<ExecuteListStreamOptions, "signal">): {
            controller: AbortController;
            result: Promise<ListExecution | null>;
        } => {
            // Close any previous stream before opening a new execution.
            abortProgress();

            const controller = new AbortController();
            abortControllerRef.current = controller;

            const result = executeListStream(listId, (next) => {
                setProgress(next);
                // Terminal events are lifecycle-end — close the channel.
                if (TERMINAL_STEPS.has(next.step)) {
                    abortProgress();
                }
            }, { signal: controller.signal, ...payload });

            return { controller, result };
        },
        [abortProgress],
    );

    return { progress, startProgress, abortProgress, resetProgress };
};
